import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useUsageTracking } from '@/hooks/useUsageTracking';
import { celebratePoints } from '@/utils/confetti';

export interface PointsTransaction {
  id: string;
  user_id: string;
  transaction_type: 'earned' | 'spent' | 'adjusted';
  points_amount: number;
  source: string;
  source_id?: string;
  description?: string;
  admin_id?: string;
  created_at: string;
}

export const usePointsTransactions = () => {
  const [transactions, setTransactions] = useState<PointsTransaction[]>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();
  const { logUsage } = useUsageTracking();

  const fetchTransactions = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('points_transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedData: PointsTransaction[] = (data || []).map(item => ({
        ...item,
        transaction_type: item.transaction_type as 'earned' | 'spent' | 'adjusted'
      }));
      setTransactions(transformedData);
      
      // Calculate total points
      const total = (data || []).reduce((sum, transaction) => {
        return sum + transaction.points_amount;
      }, 0);
      setTotalPoints(total);
    } catch (error) {
      console.error('Error fetching points transactions:', error);
      toast({
        title: "Error",
        description: "Failed to load points history",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addPointsTransaction = async (
    transactionType: 'earned' | 'spent' | 'adjusted',
    pointsAmount: number,
    source: string,
    sourceId?: string,
    description?: string
  ) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('points_transactions')
        .insert({
          user_id: user.id,
          transaction_type: transactionType,
          points_amount: pointsAmount,
          source,
          source_id: sourceId,
          description,
        });

      if (error) throw error;

      // Log usage tracking for earned points
      if (transactionType === 'earned') {
        logUsage('points_earned', { points: pointsAmount, source });
        
        // Celebrate with confetti!
        celebratePoints(pointsAmount);
        
        // Show toast with celebration
        toast({
          title: `🎉 +${pointsAmount} Points!`,
          description: description || `You earned ${pointsAmount} points`,
        });
      }

      await fetchTransactions(); // Refresh data
      return true;
    } catch (error) {
      console.error('Error adding points transaction:', error);
      toast({
        title: "Error",
        description: "Failed to update points",
        variant: "destructive",
      });
      return false;
    }
  };

  const earnPoints = (points: number, source: string, sourceId?: string, description?: string) => {
    return addPointsTransaction('earned', points, source, sourceId, description);
  };

  const spendPoints = (points: number, source: string, sourceId?: string, description?: string) => {
    return addPointsTransaction('spent', -points, source, sourceId, description);
  };

  useEffect(() => {
    fetchTransactions();
  }, [user]);

  return {
    transactions,
    totalPoints,
    loading,
    refetch: fetchTransactions,
    earnPoints,
    spendPoints,
    addPointsTransaction,
  };
};